import styled, { keyframes } from 'styled-components'
import { Identity, ValueLine } from 'components/sto/chart/chart.styles'
import { SkeletonBlock } from 'components/sto/Section'
import { stoColor, stoMedia, stoRadius, stoShadow, stoSpace } from 'constants/stoTheme'

const DURATION = '360ms'
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'

/**
 * Figma: the summary card grows into the full chart card, 8px radius in both. From 1440 the
 * summary takes half the column (32px gap to the empty half); below 1440 it is full width (3182:19321).
 */
export const Card = styled.div<{ $open: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: ${({ $open }) => ($open ? '100%' : `calc(50% - ${stoSpace[4]})`)};
  /* Only the bottom grows when open, so the header stays put and the card expands downwards. */
  padding: 0 ${stoSpace[4]} ${({ $open }) => ($open ? stoSpace[4] : '0')};
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};
  box-shadow: ${stoShadow.sm};
  transition:
    width ${DURATION} ${EASING},
    padding ${DURATION} ${EASING};

  ${stoMedia.belowXlDesktop} {
    width: 100%;
  }

  /* As the metric tiles above it on a phone. */
  ${stoMedia.belowTablet} {
    padding: 0 ${stoSpace[3]} ${({ $open }) => ($open ? stoSpace[3] : '0')};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/**
 * Figma 3356:77170: text, sparkline and button sit flush; the sparkline fills between them.
 * `$clickable`: collapsed, the whole row opens the chart.
 */
export const Header = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  min-height: 68px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'auto')};

  ${stoMedia.belowMobile} {
    gap: ${stoSpace[3]};
  }

  /* The text keeps its width; the sparkline takes what is left. */
  > ${Identity} {
    flex: 0 1 auto;
  }

  /* Figma 3582:128118: the hero card reads smaller than the performance chart headers. */
  ${stoMedia.belowDesktop} {
    ${ValueLine} {
      font-size: 14px;
      line-height: 20px;
    }
  }
`

/** Crossfades: the sparkline in the collapsed card, the range tabs in the open one. */
export const Swap = styled.div<{ $visible: boolean }>`
  flex: 0 0 auto;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 200ms ${EASING};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/**
 * Figma 3356:77170: the summary card's sparkline fills from the end of the text to the expand
 * button: faint by the figures, full in the middle, fading out just before the button. Open,
 * it stays as the spacer that pushes the range tabs right.
 */
export const Sparkline = styled(Swap)`
  flex: 1 1 0;
  align-self: stretch;
  min-width: 0;
  height: 68px;
  mask-image: linear-gradient(
    to right,
    transparent,
    rgba(0, 0, 0, 0.35) 20%,
    #000 55%,
    #000 calc(100% - 40px),
    transparent calc(100% - 6px)
  );

  /*
   * Mobile has no button: the line runs on to the card's edge, past its padding and 1px border.
   * Highcharts rounds its width down; the card's overflow clips the extra pixel at the border.
   */
  ${stoMedia.belowDesktop} {
    margin-right: calc(-${stoSpace[4]} - 1px);
    mask-image: linear-gradient(to right, transparent, rgba(0, 0, 0, 0.35) 20%, #000 55%);
  }

  ${stoMedia.belowTablet} {
    margin-right: calc(-${stoSpace[3]} - 1px);
  }

  /* A phone leaves it little room: a minimum width, and a shorter fade so the line still shows. */
  ${stoMedia.belowMobile} {
    min-width: 64px;
    mask-image: linear-gradient(to right, transparent, #000 30%);
  }
`

/** The range tabs' place in the header; while closed it takes no room from the title. */
export const HeaderSlot = styled.div<{ $open: boolean }>`
  position: ${({ $open }) => ($open ? 'relative' : 'absolute')};
  z-index: 1;
  flex: 0 0 auto;
  margin-right: ${stoSpace[3]};
  ${({ $open }) => ($open ? '' : 'right: 0; visibility: hidden;')}
`

export const ToggleButton = styled.button`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 39px;
  height: 39px;
  padding: ${stoSpace[2]};
  position: relative;
  z-index: 1;
  margin-left: auto;
  border: 2px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.full};
  background: ${stoColor.surface};
  color: ${stoColor.textPrimary};
  cursor: pointer;
  transition: border-color 150ms ${EASING};

  /* Mobile: the whole row opens and closes the chart. */
  ${stoMedia.belowDesktop} {
    display: none;
  }

  &:focus {
    outline: none;
  }

  /* Pointer devices only: on touch a tap leaves :hover stuck, the border black until the next tap. */
  @media (hover: hover) {
    &:hover {
      border-color: ${stoColor.textPrimary};
    }
  }

  /* Keyboard focus only, so a click does not leave a ring behind. */
  &:focus-visible {
    border-color: ${stoColor.textPrimary};
    box-shadow:
      0 0 0 2px ${stoColor.surface},
      0 0 0 4px ${stoColor.primary};
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

/** The full chart, appearing once the card has widened. */
export const ChartFadeIn = styled.div`
  animation: ${fadeIn} 200ms ${EASING} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

/** Height animates without measuring: grid rows 0fr -> 1fr, as in the Collapsible. */
export const Body = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition:
    grid-template-rows ${DURATION} ${EASING},
    opacity ${DURATION} ${EASING};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const BodyClip = styled.div`
  min-height: 0;
  overflow: hidden;

  /* A phone's chart runs on to the card's right edge, past its padding. */
  ${stoMedia.belowTablet} {
    margin-right: -${stoSpace[3]};
  }
`

/** The chart, and on mobile the range tabs under it. */
export const BodyInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[2]};
  padding-top: ${stoSpace[5]};

  ${stoMedia.belowTablet} {
    padding-right: ${stoSpace[3]};

    > ${ChartFadeIn} {
      margin-right: -${stoSpace[3]};
    }
  }
`

/** While the chart's data loads: the label and the value, at their own sizes. */
export const LabelSkeleton = styled(SkeletonBlock)`
  display: inline-block;
  width: 96px;
  vertical-align: middle;
`

export const ValueSkeleton = styled(SkeletonBlock)`
  width: 136px;
  margin-top: 4px;
`

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[4]};
`

/** The date and source at the foot of the open card, under the chart, and a note on the data under them. */
export const CardFootnote = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[2]};
  padding-top: ${stoSpace[3]};

  &:empty {
    display: none;
  }
`
